import { PRODUCTS, TOUR, type Family } from "@/content/quikit";
import { PRODUCT_PAGES } from "@/lib/links";

export type MenuId = "products";

export type MenuLink = { label: string; href: string; soon?: boolean };
export type MenuGroup = { title: string; links: MenuLink[] };

export type Menu = {
  id: MenuId;
  label: string;
  headline: { label: string; href: string };
  /** Catalogue order — used wherever the groups render as one list (the mobile menu). */
  groups: MenuGroup[];
  /** The same groups dealt into balanced desktop columns. */
  columns: MenuGroup[][];
};

const TOUR_KEYS = new Set(TOUR.map((t) => t.key));

/** Where a product links: its dedicated page if it has one, else its homepage section. */
export function productHref(key: string) {
  if (PRODUCT_PAGES[key]) return PRODUCT_PAGES[key];
  const p = PRODUCTS.find((x) => x.key === key);
  if (TOUR_KEYS.has(key)) return `#story-${key}`;
  if (p?.family === "QuikOffice") return "#office";
  return "#suite";
}

const group = (family: Family): MenuGroup => ({
  title: family,
  links: PRODUCTS.filter((p) => p.family === family).map((p) => ({
    label: p.name,
    href: productHref(p.key),
    soon: p.status === "soon",
  })),
});

const PRODUCT_GROUPS = (
  [
    "Work & Projects",
    "Goals & Performance",
    "People & HR",
    "Sales & Customer Success",
    "Marketing",
    "QuikOffice",
    "Platform & Technology",
    "Customer support",
  ] satisfies Family[]
).map(group);

const byTitle = (family: Family) => PRODUCT_GROUPS.find((g) => g.title === family)!;

export const MENUS: Menu[] = [
  {
    id: "products",
    label: "Products",
    // The apps index, not the homepage's six-card showcase: the label promises all of
    // them and only that page delivers all of them.
    headline: { label: `Explore all ${PRODUCTS.length} apps`, href: "/products" },
    groups: PRODUCT_GROUPS,
    // Dealt so the four columns finish at roughly the same depth; QuikOffice is the
    // longest family, so it takes a column of its own. Shipped families lead — Platform
    // & Technology is almost all "Soon", so it reads poorly in the opening column.
    columns: [
      [byTitle("Work & Projects"), byTitle("Goals & Performance"), byTitle("Customer support")],
      [byTitle("People & HR"), byTitle("Marketing")],
      [byTitle("Sales & Customer Success"), byTitle("Platform & Technology")],
      [byTitle("QuikOffice")],
    ],
  },
];


/**
 * Plain links in the bar: the left group follows the Products menu, the right group sits
 * before Log in. Everything lives on the left now, so the right side is just the account
 * actions — the order here is the order they appear, in the bar and in the mobile menu.
 */
export const LEFT_LINKS = [
  { label: "About", href: "/about" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];
export const RIGHT_LINKS: { label: string; href: string }[] = [];
